import {Key} from '../../libs/Key.js';
import * as storage from '../utils/LocalStorageWrapper.js';
import {Bindings, bindingFromEvent, bindingId, normalizeCode, gameActions} from './bindings.js';

const STORAGE_KEY = 'jollyworld-controls-v1';
export function readGamepads() {
    try { return Array.from(navigator.getGamepads?.() || []).filter(pad => pad?.connected); }
    catch { return []; }
}
export function gamepadInputs(pad) {
    const inputs = new Set();
    pad?.buttons.forEach((button, index) => {
        if (button.pressed || button.value > 0.5) inputs.add(`button:${index}`);
    });
    pad?.axes.forEach((value, index) => {
        if (Math.abs(value) > 0.5) inputs.add(`axis:${index}:${value < 0 ? '-' : '+'}`);
    });
    return inputs;
}

export class InputManager {
    constructor() {
        let saved;
        try { saved = JSON.parse(storage.getItem(STORAGE_KEY)); } catch { /* Use defaults for invalid/old storage. */ }
        this.bindings = new Bindings(saved);
        this.blocked = false;
        this.mobile = false;
        this.gamepad = null;
        this.listeners = new Set();
        this.heldEditorKeys = new Map();
        this.down = new Set();
        this.pressed = new Set();
        this.released = new Set();
        this.padPressed = new Set();
        this.padDown = new Set();
        this.previousPadInputs = new Set();
        this.ignoredPadInputs = new Set();
        this.capture = null;
        this.queuedPresses = new Set();
        // Mouse movement and pointer lock as the simulation sees them, once per
        // simulated frame, so replays can record and restore them.
        this.releasedByReset = new Set();
        this.pendingPointer = {x: 0, y: 0, moved: false};
        this.pointerMovement = {x: 0, y: 0, moved: false};
        this.pointerLocked = false;
    }
    addPointerMovement(event) {
        const x = event.movementX || 0, y = event.movementY || 0;
        this.pendingPointer.x += x;
        this.pendingPointer.y += y;
        if (Math.abs(x) > 0.1 || Math.abs(y) > 0.1) this.pendingPointer.moved = true;
    }
    // Screen pixels moved this frame, divided by the zoom they were made at.
    applyPointerMovement(x, y, moved, scaleX, scaleY) {
        this.pointerMovement.x = x / scaleX;
        this.pointerMovement.y = y / scaleY;
        this.pointerMovement.moved = moved;
    }
    clearPointerMovement() {
        this.pendingPointer.x = this.pendingPointer.y = 0;
        this.pendingPointer.moved = false;
    }
    save() { storage.setItem(STORAGE_KEY, JSON.stringify(this.bindings.values)); }
    setBinding(group, action, slot, binding) {
        const displaced = this.bindings.set(group, action, slot, binding);
        this.save();
        this.reset();
        return displaced;
    }
    resetBindings(group) { this.bindings.reset(group); this.save(); this.reset(); }
    reset() {
        // Reported by the next update, so the release happens inside a simulated frame.
        for (const id of this.down) this.releasedByReset.add(id);
        Key.reset();
        this.down.clear();
        this.pressed.clear();
        this.released.clear();
        this.padDown.clear();
        this.padPressed.clear();
        this.heldEditorKeys.clear();
        this.queuedPresses.clear();
        // A button held while closing a panel or restoring focus must be released
        // before it can pause/restart the game again.
        this.ignoredPadInputs = gamepadInputs(this.gamepad);
        this.onReset?.();
    }
    setBlocked(blocked) { this.blocked = blocked; this.capture = null; this.reset(); }
    match(group, event) {
        return (this.mobile ? defaultBindings : this.bindings).match(group, event)?.id;
    }
    keyboardDown(action, source = 'keyboard') {
        const bindings = (this.mobile || source === 'gamepad') ? defaultBindings : this.bindings;
        const isDown = code => Key.isDown(code, source);
        const ctrlKey = [17, 91, 93, 224].some(isDown), shiftKey = isDown(16), altKey = isDown(18);
        return bindings.values.game[action].some(binding => {
            if (!binding) return false;
            const code = normalizeCode(binding.code);
            const held = code === 17 ? ctrlKey : isDown(code);
            const exact = bindings.match('game', {keyCode: code, ctrlKey, shiftKey, altKey})?.id;
            if (exact && exact !== action) return false;
            return held && (!binding.ctrl || ctrlKey) && (!binding.shift || shiftKey) && (!binding.alt || altKey);
        });
    }
    update({enabled = true} = {}) {
        const pads = this.mobile ? [] : readGamepads();
        const pad = pads.find(pad => pad.index === this.gamepad?.index) || pads[0] || null;
        const changed = pad?.id !== this.gamepad?.id || pad?.index !== this.gamepad?.index;
        this.gamepad = pad;
        if (changed) {
            this.capture = null;
            this.previousPadInputs.clear();
            this.ignoredPadInputs = gamepadInputs(pad);
            this.listeners.forEach(listener => listener(pad));
        }
        const raw = gamepadInputs(pad);
        if (this.capture) {
            const fresh = [...raw].find(value => !this.previousPadInputs.has(value));
            if (fresh) { const callback = this.capture; this.capture = null; callback(fresh); }
        }
        this.previousPadInputs = raw;
        for (const value of this.ignoredPadInputs) if (!raw.has(value)) this.ignoredPadInputs.delete(value);
        const keys = new Set();
        if (!this.blocked && enabled) {
            for (const {id, defaults} of gameActions) {
                if (!this.bindings.values.gamepad[id].some(binding => raw.has(binding) && !this.ignoredPadInputs.has(binding))) continue;
                // Authored levels listen for the original keyboard codes. Emit
                // both defaults (e.g. Up and W), independent of user remapping.
                for (const binding of defaults) {
                    if (!binding) continue;
                    keys.add(binding.code);
                    if (binding.ctrl) keys.add(17);
                    if (binding.shift) keys.add(16);
                    if (binding.alt) keys.add(18);
                }
            }
        } else {
            this.ignoredPadInputs = new Set(raw);
        }
        Key.setGamepadKeys(keys);
        const nextPad = new Set();
        const next = new Set();
        if (!this.blocked && enabled) {
            for (const {id} of gameActions) {
                if (this.keyboardDown(id, 'gamepad')) nextPad.add(id);
                if (nextPad.has(id) || this.keyboardDown(id)) next.add(id);
            }
        }
        this.padPressed = new Set([...nextPad].filter(id => !this.padDown.has(id)));
        this.pressed = new Set([...next].filter(id => !this.down.has(id)));
        if (!this.blocked && enabled) for (const id of this.queuedPresses) this.pressed.add(id);
        this.queuedPresses.clear();
        this.released = new Set([...this.down, ...this.releasedByReset].filter(id => !next.has(id)));
        this.releasedByReset.clear();
        this.padDown = nextPad;
        this.down = next;
    }
    isDown(action) { return this.down.has(action); }
    isPressed(action) { return this.pressed.has(action); }
    isReleased(action) { return this.released.has(action); }
    queuePress(action) { if (action && !this.down.has(action)) this.queuedPresses.add(action); }
    startGamepadCapture(callback) { this.previousPadInputs = gamepadInputs(this.gamepad); this.capture = callback; }
    cancelCapture() { this.capture = null; }
    editorEvent(event, down) {
        const physical = event.keyCode;
        let canonical;
        if (down) {
            const held = [...this.heldEditorKeys.values()].map(binding => binding.code);
            const modified = {keyCode: physical, ctrlKey: event.ctrlKey || event.metaKey || held.includes(17),
                shiftKey: event.shiftKey || held.includes(16), altKey: event.altKey || held.includes(18)};
            // Standalone hold actions must also work while another modifier is held.
            const plain = this.bindings.match('editor', {keyCode: physical});
            const action = this.bindings.match('editor', modified) || (['shift', 'control', 'alt', 'pan'].includes(plain?.id) ? plain : null);
            if (!action || action.id === 'test') return null;
            const pressed = bindingFromEvent(event);
            canonical = action.defaults.find(b => b && bindingId(b) === bindingId(pressed)) || action.defaults[0];
            canonical = {
                ...canonical,
                shift: !!canonical.shift || !!(action.shiftable && modified.shiftKey),
                alt: !!canonical.alt || !!(action.shiftable && modified.altKey),
            };
            this.heldEditorKeys.set(physical, canonical);
        } else {
            canonical = this.heldEditorKeys.get(physical);
            this.heldEditorKeys.delete(physical);
            if ([91, 93, 224].includes(physical)) this.heldEditorKeys.clear();
        }
        if (!canonical) return null;
        return {
            keyCode: canonical.code, key: String.fromCharCode(canonical.code),
            ctrlKey: !!canonical.ctrl, metaKey: false, shiftKey: !!canonical.shift, altKey: !!canonical.alt,
            preventDefault: () => event.preventDefault(),
        };
    }
    syncEditorModifiers(editor) {
        const held = [...this.heldEditorKeys.values()].map(binding => binding.code);
        editor.shiftDown = held.includes(16);
        editor.ctrlDown = held.includes(17);
        editor.altDown = held.includes(18);
        editor.spaceDown = held.includes(32);
    }
}

const defaultBindings = new Bindings();
export const input = new InputManager();
